import type { MessagePreviewView } from '@today/ui-domain'
import { useRef, useState } from 'react'
import { templatePreviewErrorOf } from '@/modules/offer-notification/api-error'
import {
	type NotificationTemplatePreviewBody,
	notificationTemplateService,
} from '@/services/notification-template.service'

/**
 * A template preview (TO-140 REQ-008 / REQ-108): renders unsaved subject and body
 * against an offer through `POST /offers/templates/preview`, showing
 * only the latest response; a refusal becomes the pane's message.
 */
export function useTemplatePreview() {
	const [preview, setPreview] = useState<MessagePreviewView | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [isPending, setPending] = useState(false)
	const seqRef = useRef(0)

	const run = (body: NotificationTemplatePreviewBody) => {
		const seq = ++seqRef.current
		setPending(true)
		notificationTemplateService
			.preview(body)
			.then((rendered) => {
				if (seq !== seqRef.current) return
				setPreview(rendered)
				setError(null)
			})
			.catch((refusal: unknown) => {
				if (seq === seqRef.current) setError(templatePreviewErrorOf(refusal))
			})
			.finally(() => {
				if (seq === seqRef.current) setPending(false)
			})
	}

	/** Forgets the shown preview (and drops any response still in flight). */
	const reset = () => {
		seqRef.current++
		setPreview(null)
		setError(null)
		setPending(false)
	}

	return { preview, error, isPending, run, reset }
}
